import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { ensureValidToken } from '../services/TokenRefreshService';
import { TokenService } from '../services/TokenService';

interface Customer {
	name: string;
	customerNumber: number;
}

interface AuthContextType {
	isAuthenticated: boolean;
	isAuthReady: boolean;
	customer: Customer | null;
	token: string | null;
	login: (bearerToken: any) => void;
	logout: () => void;
	isTokenExpiringSoon: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
	children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
	const [isAuthenticated, setIsAuthenticated] = useState<boolean>(TokenService.isAuthenticated());
	const [customer, setCustomer] = useState<Customer | null>(TokenService.getCustomer());
	const [token, setToken] = useState<string | null>(TokenService.getToken());
	const [isTokenExpiringSoon, setIsTokenExpiringSoon] = useState<boolean>(TokenService.isTokenExpiringSoon());
	const [isAuthReady, setIsAuthReady] = useState(false);

	useEffect(() => {
		let isActive = true;

		const syncAuthState = () => {
			const authenticated = TokenService.isAuthenticated();
			const currentCustomer = TokenService.getCustomer();
			const currentToken = TokenService.getToken();
			const expiringSoon = TokenService.isTokenExpiringSoon();

			setIsAuthenticated(authenticated);
			setCustomer(currentCustomer);
			setToken(currentToken);
			setIsTokenExpiringSoon(expiringSoon);
		};

		const refreshAndSync = async () => {
			try {
				await ensureValidToken(5);
			} catch (error) {
				console.error('Initial token refresh failed:', error);
			} finally {
				if (isActive) {
					syncAuthState();
					setIsAuthReady(true);
				}
			}
		};

		const unsubscribe = TokenService.subscribe(() => {
			if (isActive) {
				syncAuthState();
			}
		});

		void refreshAndSync();

		const interval = setInterval(() => {
			void ensureValidToken(5)
				.catch(error => {
					console.error('Scheduled token refresh failed:', error);
				})
				.finally(() => {
					if (isActive) {
						syncAuthState();
					}
				});
		}, 60000);

		return () => {
			isActive = false;
			unsubscribe();
			clearInterval(interval);
		};
	}, []);

	const login = (bearerToken: any) => {
		try {
			TokenService.saveToken(bearerToken);
			setIsAuthenticated(true);
			setCustomer(bearerToken.customer);
			setToken(bearerToken.token);
			setIsTokenExpiringSoon(false);

			console.log('User logged in:', bearerToken.customer.name);
		} catch (error) {
			console.error('Login failed:', error);
		}
	};

	const logout = () => {
		TokenService.clearToken();
		setIsAuthenticated(false);
		setCustomer(null);
		setToken(null);
		setIsTokenExpiringSoon(false);

		console.log('User logged out');
	};

	return (
		<AuthContext.Provider
			value={{
				isAuthenticated,
				isAuthReady,
				customer,
				token,
				login,
				logout,
				isTokenExpiringSoon
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};

export const useAuth = (): AuthContextType => {
	const context = useContext(AuthContext);
	if (context === undefined) {
		throw new Error('useAuth must be used within an AuthProvider');
	}
	return context;
};